/**
 * Os dois pedidos da recorrência automática de ajuda de custo.
 *
 * Papel: aqui ficam `useGerarRecorrencia`, que pede a recorrência e avisa o que
 * ela gerou, e `useRecorrenciaAoAbrir`, que faz esse pedido quando a aba
 * Lançamentos fica à vista, uma vez por dia — o `_checarRecorrenciaAjudaCustoHoje`
 * da origem (l. 5241–5246), que `renderLancamentos` chamava.
 *
 * Como usar: a aba Lançamentos chama `useRecorrenciaAoAbrir` com a casa e a
 * permissão; "Salvar Perfil" chama a função de `useGerarRecorrencia` com
 * `forcar: true`, que passa por cima da trava do dia no servidor. A falha é
 * silenciosa, como na origem: nenhum aviso, e a próxima abertura tenta de novo.
 */
import { useCallback, useEffect } from 'react';

import { useAbaVisivel, useNotificacao } from '@/shared/embutido';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  avisoDaRecorrencia,
  desfazerVerificacaoDoDia,
  pedirVerificacaoDoDia,
} from '../recorrencia';
import { useVerificarRecorrencia } from './usar-financas-extras';

/**
 * Devolve a função que pede a recorrência e avisa o que ela gerou.
 *
 * @returns A função, que recebe a casa e o `forcar` e cumpre com verdadeiro
 *   quando a rota respondeu e com falso quando falhou.
 */
export function useGerarRecorrencia() {
  const verificar = useVerificarRecorrencia().mutateAsync;
  const notificar = useNotificacao();
  return useCallback(
    async (residenciaId: string, forcar: boolean): Promise<boolean> => {
      try {
        const aviso = avisoDaRecorrencia(await verificar({ residenciaId, forcar }));
        if (aviso !== null) notificar(aviso);
        return true;
      } catch {
        return false;
      }
    },
    [verificar, notificar],
  );
}

/**
 * Pede a recorrência quando a aba fica à vista, uma vez por dia.
 *
 * @param residenciaId - A casa do caixa; nula não pede nada.
 * @param podeEditar - Falso para quem só tem `ver`, que nunca pede.
 */
export function useRecorrenciaAoAbrir(residenciaId: string | null, podeEditar: boolean) {
  const visivel = useAbaVisivel();
  const gerar = useGerarRecorrencia();

  useEffect(() => {
    if (!visivel || !podeEditar || residenciaId === null) return;
    if (!pedirVerificacaoDoDia(hojeEmIso())) return;
    void gerar(residenciaId, false).then((respondeu) => {
      if (!respondeu) desfazerVerificacaoDoDia();
    });
  }, [visivel, podeEditar, residenciaId, gerar]);
}
